<template>
  <section class="ai-section ai-grid">
    <aside class="ai-panel ai-panel--sidebar">
      <div class="ai-panel__header">
        <div>
          <h2 class="ai-panel__title">Inbox review</h2>
          <p class="ai-panel__subtitle">Sugestiile AI intră aici înainte de orice mutație în CRM.</p>
        </div>
        <v-btn icon variant="text" size="small" :loading="reviewLoading" @click="refreshPanel">
          <v-icon>mdi-refresh</v-icon>
        </v-btn>
      </div>
      <div class="ai-toolbar">
        <v-select
          v-model="reviewFilters.status"
          :items="reviewStatusOptions"
          item-title="label"
          item-value="value"
          label="Status review"
          hide-details
          @update:model-value="handleFilterChange"
        />
        <v-select
          v-model="reviewFilters.entityType"
          :items="entityTypeOptions"
          item-title="label"
          item-value="value"
          label="Entitate"
          hide-details
          @update:model-value="handleFilterChange"
        />
      </div>
      <div v-if="reviewLoading" class="ai-loading">
        <v-progress-circular indeterminate color="primary" />
      </div>
      <div v-else-if="!reviewItems.length" class="ai-empty">
        Nu există review items pentru filtrele curente.
      </div>
      <div v-else class="ai-list">
        <button
          v-for="item in reviewItems"
          :key="item.id"
          type="button"
          class="ai-list-card"
          :class="{ 'ai-list-card--active': selectedReviewId === item.id }"
          @click="selectReview(item.id)"
        >
          <div class="ai-list-card__meta">
            <span class="ai-list-card__entity">{{ formatEntityLabel(item.entity_type, item.entity_id) }}</span>
            <v-chip :color="reviewStatusColor(item.status)">
              {{ formatReviewStatus(item.status) }}
            </v-chip>
          </div>
          <strong class="ai-list-card__title">
            {{ item.review_summary || "Fără sumar generat" }}
          </strong>
          <div class="ai-list-card__footer">
            <span>{{ item.profile_key }}</span>
            <span>
              {{ item.suggestion_summary?.pending_count || 0 }} pending /
              {{ item.suggestion_summary?.approved_count || 0 }} ready
            </span>
          </div>
        </button>
      </div>
    </aside>
    <div class="ai-panel ai-panel--content">
      <div v-if="reviewDetailLoading" class="ai-loading">
        <v-progress-circular indeterminate color="primary" />
      </div>
      <div v-else-if="!reviewDetail" class="ai-empty ai-empty--large">
        Selectează un review item pentru a vedea diferențele pe câmpuri.
      </div>
      <div v-else class="ai-review-detail">
        <div class="ai-panel__header">
          <div>
            <h2 class="ai-panel__title">{{ reviewDetail.review_item.review_summary || "Review AI" }}</h2>
            <p class="ai-panel__subtitle">
              {{ formatEntityLabel(reviewDetail.review_item.entity_type, reviewDetail.review_item.entity_id) }}
              · profil {{ reviewDetail.review_item.profile_key }}
            </p>
          </div>
          <div class="ai-action-row">
            <v-chip :color="reviewStatusColor(reviewDetail.review_item.status)">
              {{ formatReviewStatus(reviewDetail.review_item.status) }}
            </v-chip>
            <v-btn
              variant="tonal"
              color="primary"
              :loading="reviewActionLoading === 'recompute'"
              @click="recomputeReviewItem"
            >
              Recompute
            </v-btn>
            <v-btn
              variant="tonal"
              color="error"
              :loading="reviewActionLoading === 'reject-item'"
              @click="rejectReviewItem"
            >
              Respinge
            </v-btn>
            <v-btn
              color="primary"
              :disabled="reviewDetail.review_item.status !== 'ready_to_apply'"
              :loading="reviewActionLoading === 'apply'"
              @click="applyReviewItem"
            >
              Aplică în CRM
            </v-btn>
          </div>
        </div>
        <div class="ai-review-summary">
          <div class="ai-summary-card">
            <span class="ai-summary-card__label">Clasificare</span>
            <pre class="ai-summary-card__value">{{ formatJsonBlock(reviewDetail.review_item.classification_json) }}</pre>
          </div>
          <div class="ai-summary-card">
            <span class="ai-summary-card__label">Citări și surse</span>
            <div v-if="reviewDetail.review_item.citations_json?.length" class="ai-citations">
              <a
                v-for="(citation, index) in reviewDetail.review_item.citations_json"
                :key="`${citation.url || 'citation'}-${index}`"
                class="ai-citation"
                :href="citation.url"
                target="_blank"
                rel="noreferrer"
              >
                {{ citation.label || citation.url || `Sursa ${index + 1}` }}
              </a>
            </div>
            <span v-else class="ai-muted">Fără citări salvate pentru acest run.</span>
          </div>
        </div>
        <div class="ai-suggestions">
          <AiReviewSuggestionCard
            v-for="suggestion in reviewDetail.field_suggestions"
            :key="suggestion.id"
            :suggestion="suggestion"
            :is-editing="editingSuggestionId === suggestion.id"
            :editing-value="editingSuggestionValue"
            :action-loading="reviewActionLoading"
            @approve="submitSuggestionDecision($event, 'approve')"
            @reject="submitSuggestionDecision($event, 'reject')"
            @start-edit="startSuggestionEdit"
            @cancel-edit="cancelSuggestionEdit"
            @save-edit="saveEditedSuggestion"
            @update:editing-value="editingSuggestionValue = $event"
          />
        </div>
      </div>
    </div>
  </section>
</template>
<script>
import axios from "axios";
import AiReviewSuggestionCard from "./AiReviewSuggestionCard.vue";
import {
  REVIEW_STATUS_OPTIONS,
  ENTITY_TYPE_OPTIONS,
  parseJsonEditorValue,
  formatJsonBlock,
  formatValueBlock,
  formatEntityLabel,
  formatReviewStatus,
  reviewStatusColor,
} from "./aiWorkspaceShared.mjs";
export default {
  name: "AiReviewPanel",
  components: {
    AiReviewSuggestionCard,
  },
  emits: ["feedback"],
  data() {
    return {
      reviewFilters: {
        status: "",
        entityType: "",
      },
      reviewStatusOptions: REVIEW_STATUS_OPTIONS,
      entityTypeOptions: ENTITY_TYPE_OPTIONS,
      reviewItems: [],
      reviewLoading: false,
      reviewDetail: null,
      reviewDetailLoading: false,
      selectedReviewId: null,
      reviewActionLoading: "",
      editingSuggestionId: null,
      editingSuggestionValue: "",
    };
  },
  watch: {
    "$route.query.reviewId"(value) {
      const nextReviewId = value ? Number(value) : null;
      if (nextReviewId && nextReviewId !== this.selectedReviewId) {
        this.selectReview(nextReviewId, false);
      }
    },
  },
  mounted() {
    this.loadReviewItems(this.resolveRouteReviewId());
  },
  methods: {
    formatJsonBlock,
    formatValueBlock,
    formatEntityLabel,
    formatReviewStatus,
    reviewStatusColor,
    emitFeedback(type, message) {
      this.$emit("feedback", { type, message });
    },
    resolveRouteReviewId() {
      return this.$route.query?.reviewId ? Number(this.$route.query.reviewId) : null;
    },
    refreshPanel() {
      return this.loadReviewItems(this.selectedReviewId || this.resolveRouteReviewId());
    },
    handleFilterChange() {
      return this.loadReviewItems(this.selectedReviewId);
    },
    resolveTargetReviewId(preferredReviewId = null) {
      const availableIds = new Set(this.reviewItems.map((item) => Number(item.id)));
      const candidateIds = [
        preferredReviewId,
        this.selectedReviewId,
        this.resolveRouteReviewId(),
        this.reviewItems?.[0]?.id,
      ];

      for (const candidate of candidateIds) {
        const normalized = Number(candidate);
        if (Number.isFinite(normalized) && availableIds.has(normalized)) {
          return normalized;
        }
      }
      return null;
    },
    async loadReviewItems(preferredReviewId = null) {
      this.reviewLoading = true;
      try {
        const params = {};
        if (this.reviewFilters.status) params.status = this.reviewFilters.status;
        if (this.reviewFilters.entityType) params.entity_type = this.reviewFilters.entityType;

        const { data } = await axios.get("/ai/review", { params });
        this.reviewItems = Array.isArray(data?.items) ? data.items : [];

        const targetReviewId = this.resolveTargetReviewId(preferredReviewId);
        if (targetReviewId) {
          await this.selectReview(targetReviewId, false);
          return;
        }

        this.selectedReviewId = null;
        this.reviewDetail = null;
        this.cancelSuggestionEdit();
      } catch (error) {
        this.emitFeedback("error", error?.response?.data?.message || "Nu s-a putut încărca inbox-ul AI.");
      } finally {
        this.reviewLoading = false;
      }
    },
    async selectReview(reviewId, updateRoute = true) {
      if (!reviewId) {
        return;
      }

      this.selectedReviewId = Number(reviewId);
      if (updateRoute) {
        this.$router.replace({
          query: {
            ...this.$route.query,
            tab: "review",
            reviewId: String(reviewId),
          },
        });
      }

      this.reviewDetailLoading = true;
      try {
        const { data } = await axios.get(`/ai/review/${reviewId}`);
        this.reviewDetail = data;
        this.cancelSuggestionEdit();
      } catch (error) {
        this.emitFeedback("error", error?.response?.data?.message || "Nu s-au putut încărca detaliile review-ului.");
      } finally {
        this.reviewDetailLoading = false;
      }
    },
    async submitSuggestionDecision(suggestion, action) {
      const actionKey = `${action}-${suggestion.id}`;
      this.reviewActionLoading = actionKey;
      try {
        await axios.post(`/ai/review/${this.reviewDetail.review_item.id}/decisions`, {
          decisions: [
            {
              suggestion_id: suggestion.id,
              action,
            },
          ],
        });
        await this.loadReviewItems(this.reviewDetail.review_item.id);
      } catch (error) {
        this.emitFeedback("error", error?.response?.data?.message || "Nu s-a putut salva decizia AI.");
      } finally {
        this.reviewActionLoading = "";
      }
    },
    startSuggestionEdit(suggestion) {
      this.editingSuggestionId = suggestion.id;
      const value =
        suggestion.decision_status === "edited" && suggestion.edited_value_json !== null
          ? suggestion.edited_value_json
          : suggestion.suggested_value_json;
      this.editingSuggestionValue = formatValueBlock(value);
    },
    cancelSuggestionEdit() {
      this.editingSuggestionId = null;
      this.editingSuggestionValue = "";
    },
    async saveEditedSuggestion(suggestion) {
      this.reviewActionLoading = `edit-${suggestion.id}`;
      try {
        await axios.post(`/ai/review/${this.reviewDetail.review_item.id}/decisions`, {
          decisions: [
            {
              suggestion_id: suggestion.id,
              action: "edit",
              edited_value: parseJsonEditorValue(this.editingSuggestionValue),
            },
          ],
        });
        this.cancelSuggestionEdit();
        await this.loadReviewItems(this.reviewDetail.review_item.id);
      } catch (error) {
        this.emitFeedback("error", error?.response?.data?.message || "Nu s-a putut salva editarea AI.");
      } finally {
        this.reviewActionLoading = "";
      }
    },
    async rejectReviewItem() {
      if (!this.reviewDetail?.review_item?.id) {
        return;
      }

      this.reviewActionLoading = "reject-item";
      try {
        await axios.post(`/ai/review/${this.reviewDetail.review_item.id}/reject`, {
          reason: "Rejected from AI workspace",
        });
        await this.loadReviewItems(this.reviewDetail.review_item.id);
      } catch (error) {
        this.emitFeedback("error", error?.response?.data?.message || "Nu s-a putut respinge review item-ul.");
      } finally {
        this.reviewActionLoading = "";
      }
    },
    async recomputeReviewItem() {
      if (!this.reviewDetail?.review_item?.id) {
        return;
      }

      this.reviewActionLoading = "recompute";
      try {
        await axios.post(`/ai/review/${this.reviewDetail.review_item.id}/recompute`);
        this.emitFeedback("success", "Run-ul AI a fost requeue-uit pentru recompute.");
        await this.loadReviewItems(this.reviewDetail.review_item.id);
      } catch (error) {
        this.emitFeedback("error", error?.response?.data?.message || "Nu s-a putut relansa review item-ul.");
      } finally {
        this.reviewActionLoading = "";
      }
    },
    async applyReviewItem() {
      if (!this.reviewDetail?.review_item?.id) {
        return;
      }

      this.reviewActionLoading = "apply";
      try {
        await axios.post(`/ai/review/${this.reviewDetail.review_item.id}/apply`);
        this.emitFeedback("success", "Sugestiile aprobate au fost aplicate în CRM.");
        await this.loadReviewItems(this.reviewDetail.review_item.id);
      } catch (error) {
        this.emitFeedback("error", error?.response?.data?.message || "Nu s-a putut aplica review-ul în CRM.");
      } finally {
        this.reviewActionLoading = "";
      }
    },
  },
};
</script>
